import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Image } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { linkedAccountsStrings } from '@/constants/residents/strings';
import { LinkedAccount } from '@/types/residents/home';

interface LinkedAccountsCardProps {
  linkedAccounts: LinkedAccount[];
  onPress: () => void;
  testID?: string;
}

export const LinkedAccountsCard: React.FC<LinkedAccountsCardProps> = ({ 
  linkedAccounts, 
  onPress,
  testID,
}) => {
  const visibleAccounts = linkedAccounts.length > 4 ? linkedAccounts.slice(0, 4) : linkedAccounts;
  const hasMoreThanFour = linkedAccounts.length > 4;

  return (
    <TouchableOpacity 
      style={styles.card} 
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={'ProfileCell_profile.option.connections'}
      accessibilityHint={'Linked Accounts - Invite people to make payments on your behalf'}
      testID={testID ?? 'ProfileCell_profile.option.connections'}
    >
      {/* Avatar Stack */}
      <View style={styles.avatarStack}>
        {visibleAccounts.map((linkedAccount, index) => {
          return (
            <View 
              key={linkedAccount.id} 
              style={[
                styles.avatar, 
                { marginLeft: index > 0 ? -10 : 0, zIndex: visibleAccounts.length - index }
              ]}
            >
              {linkedAccount.user.photoUrl ? (
                <Image 
                  source={{ uri: linkedAccount.user.photoUrl }} 
                  style={styles.avatarImage}
                  resizeMode="cover"
                />
              ) : (
                <Icon name="person" size={20} color={MARKETPLACE_COLORS.textPrimary} />
              )}
            </View>
          );
        })}
        {hasMoreThanFour && (
          <View style={[styles.avatar, styles.plusAvatar]}>
            <Icon name="add" size={16} color={MARKETPLACE_COLORS.textPrimary} />
          </View>
        )}
      </View>

      {/* Chevron - positioned at top-right */}
      <Icon 
        name="chevron-forward" 
        size={24} 
        color={MARKETPLACE_COLORS.inputBorder}
        style={styles.chevron}
      />

      {/* Title */}
      <Text style={styles.title}>{linkedAccountsStrings.cardTitle}</Text>

      {/* Description */}
      <Text style={styles.description} numberOfLines={0}>
        {linkedAccountsStrings.cardDescription}
      </Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderRadius: 16,
    padding: 20,
    position: 'relative',
  },
  avatarStack: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginBottom: 8,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: MARKETPLACE_COLORS.white10Percent,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: MARKETPLACE_COLORS.cardBackground,
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 18,
  },
  plusAvatar: {
    backgroundColor: MARKETPLACE_COLORS.inputBorder,
  },
  chevron: {
    position: 'absolute',
    top: 20,
    right: 20,
  },
  title: {
    fontSize: 13,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    marginBottom: 2,
  },
  description: {
    fontSize: 13,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textMuted,
    paddingRight: 24, // Avoid chevron overlap
  },
});
